import { useState, useMemo } from 'react';
import { useLang, getMonths } from '../../config/i18n';
import { MONTHS, OP_STATUS, CURRENT_MONTH_KEY } from '../../config/constants';
import { Modal, Field, StatCard, SaveBtn } from '../shared/UI';
import { OverheadTab } from './Finance';

export default function MonthlyTab({ contracts = [], monthly, setMonthly, tenants, ownership, overhead = [], setOverhead = ()=>{} }){
  const [sub, setSub] = useState('ops'); // 'ops' | 'ga'

  if (sub === 'ga'){
    return (
      <div style={{minHeight:"100vh",background:"#0d0f1a"}}>
        <SubNav sub={sub} setSub={setSub}/>
        <OverheadTab overhead={overhead} setOverhead={setOverhead}/>
      </div>
    );
  }

  return (
    <div style={{minHeight:"100vh",background:"#0d0f1a"}}>
      <SubNav sub={sub} setSub={setSub}/>
      <MonthlyOpsBody contracts={contracts} monthly={monthly} setMonthly={setMonthly} tenants={tenants} ownership={ownership} overhead={overhead}/>
    </div>
  );
}

function SubNav({ sub, setSub }){
  const SUB = [
    { key:'ops', label:'📅 التشغيل الشهري' },
    { key:'ga',  label:'🏢 المصاريف الإدارية' },
  ];
  return (
    <div style={{background:"rgba(0,0,0,0.2)",borderBottom:"1px solid rgba(255,255,255,0.06)",padding:"12px 20px",display:"flex",gap:8,overflowX:"auto"}}>
      <span style={{fontSize:14,fontWeight:800,color:"#c8a03c",marginLeft:12,flexShrink:0,alignSelf:"center"}}>🗓️ العمليات</span>
      {SUB.map(s => (
        <button key={s.key} onClick={()=>setSub(s.key)}
          style={{padding:"7px 16px",borderRadius:10,border:"1px solid",cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"'Tajawal',sans-serif",flexShrink:0,
            background:sub===s.key?"rgba(200,160,60,0.18)":"rgba(255,255,255,0.03)",
            color:sub===s.key?"#c8a03c":"#64748b",
            borderColor:sub===s.key?"rgba(200,160,60,0.4)":"rgba(255,255,255,0.07)"}}>
          {s.label}
        </button>
      ))}
    </div>
  );
}

function MonthlyOpsBody({ contracts = [], monthly, setMonthly, tenants, ownership, overhead = [] }){
  const {S, lang, dir, font} = useLang();
  const T = S.monthly;
  const [selected,    setSelected]   = useState(null);
  const [form,        setForm]       = useState({});
  const [viewMonth,   setViewMonth]  = useState(CURRENT_MONTH_KEY);
  const [maintModal,  setMaintModal] = useState(null);  // { unitId, monthKey }
  const [newItem,     setNewItem]    = useState({ desc:"", amount:"" });

  const curMonth = MONTHS.find(m=>m.key===viewMonth);

  // Contracts are the source of truth for units. Storage warehouses are skipped.
  // Archived contracts whose data is still relevant this fiscal year contribute historical cells,
  // but we DEDUPE by unitNumber+district so a renewed unit (archived + new contract for same unit)
  // shows as one row. The display id is the newest contract; the row remembers prior ids so
  // historical months (keyed under old contract ids) still surface.
  const _yearStart = MONTHS[0]?.key + '-01';
  const _relevantContracts = (contracts||[])
    .filter(c => c.type !== 'storage' && (c.status !== 'archived' || !c.contractEnd || c.contractEnd >= _yearStart));
  const _grouped = {};
  _relevantContracts.forEach(c => {
    const key = `${(c.unitNumber||c.unitName||'').trim()}|${(c.district||'').trim()}`;
    if (!_grouped[key]) _grouped[key] = [];
    _grouped[key].push(c);
  });
  const unitRows = Object.values(_grouped).map(group => {
    // Pick the newest contract (prefer non-archived; then latest contractEnd / contractStart).
    const sorted = [...group].sort((a,b) => {
      if ((a.status==='archived')!==(b.status==='archived')) return a.status==='archived' ? 1 : -1;
      const aEnd = a.contractEnd || a.contractStart || '';
      const bEnd = b.contractEnd || b.contractStart || '';
      return bEnd.localeCompare(aEnd);
    });
    const main = sorted[0];
    const relatedIds = group.map(c => c.id).filter(id => id !== main.id);
    // Union of all contract windows for this unit (main + archived prior contracts).
    // A month is "within window" if it falls inside ANY of the contracts' date ranges.
    const windows = group
      .filter(c => c.contractStart || c.contractEnd)
      .map(c => ({ start: c.contractStart||'', end: c.contractEnd||'' }));
    return {
      id:         main.id,
      unitNumber: main.unitNumber || main.unitName || '',
      district:   main.district || '',
      archived:   main.status === 'archived',
      contractEnd: main.contractEnd || '',
      relatedIds,
      windows,
    };
  });

  // Returns true if monthKey (YYYY-MM) overlaps any of the unit's contract windows.
  function monthInContract(row, monthKey){
    if (!row || !row.windows || row.windows.length === 0) return true; // no dates = assume always
    const mStart = monthKey + '-01';
    const mEnd   = monthKey + '-31';
    return row.windows.some(w =>
      (!w.start || w.start <= mEnd) && (!w.end || w.end >= mStart)
    );
  }

  function getOp(unitId, monthKey){
    const row = unitRows.find(u => u.id === unitId);
    // Single source of truth: read the exact (contract id, month) key. No sibling fallback.
    // Data migration guarantees archived-sibling entries are moved to the current contract id.
    const raw = monthly[`${unitId}-${monthKey}`];
    const inContract = monthInContract(row, monthKey);
    const data = raw || {status: inContract ? "شاغرة" : "—",tenantName:"",rentCollected:0,electricityCost:0,maintenanceCost:0,maintenanceItems:[],notes:"",tenants:[]};
    if (!inContract) return { ...data, status: '—' };
    if (data.status !== 'مشغول') return { ...data, status: 'شاغرة' };
    return data;
  }
  // Returns the list of tenants for a month op. Supports multiple tenants per unit per month
  // (e.g. a short-stay followed by another renter). Falls back to legacy single-tenant fields.
  function tenantsOf(op){
    if (Array.isArray(op?.tenants) && op.tenants.length) return op.tenants;
    if (op?.tenantName || op?.rentCollected) return [{ id:'legacy', name: op.tenantName||'', amount: Number(op.rentCollected)||0 }];
    return [];
  }
  function totalRentOf(op){ return tenantsOf(op).reduce((s,t)=>s+(Number(t.amount)||0),0); }

  // Auto-calculate maintenanceCost from items
  function calcMaintTotal(items){ return (items||[]).reduce((s,i)=>s+Number(i.amount||0),0); }

  // Snapshot the current contract's annual lease cost onto the monthly record. This freezes
  // history: renewing the contract later with a new rent won't rewrite past months' fixed cost.
  // Reads a stored snapshot when present; otherwise falls back to the live contract value.
  function contractSnapshot(cid){
    const c = (contracts||[]).find(x => x.id === cid);
    if(!c) return {};
    return {
      leaseCostAtRecord: (Number(c.annualRent)||0) + (Number(c.officeFee)||0) + (Number(c.waterAnnual)||0),
      ejarAtRecord:      c.ejarNum || '',
    };
  }

  function openCell(unitId, monthKey){
    const op=getOp(unitId,monthKey);
    const seedList = tenantsOf(op);
    // Always start with an empty row if no stored tenants. NEVER auto-populate from the legacy
    // tenants array — doing so bakes a ghost name into cells the user only opened to review.
    const formTenants = seedList.length ? seedList.map(t=>({...t, amount:String(t.amount||'')}))
      : [{ id: Date.now(), name: '', amount: '' }];
    setForm({...op, tenants: formTenants, maintenanceItems:op.maintenanceItems||[]});
    setSelected({unitId,monthKey});
  }

  function saveOp(){
    const key=`${selected.unitId}-${selected.monthKey}`;
    const items=form.maintenanceItems||[];
    const existing = getOp(selected.unitId, selected.monthKey);
    // Clean the tenants list: drop fully-empty rows, coerce amounts.
    const cleanTenants = (form.tenants||[])
      .map(t=>({ id: t.id || Date.now()+Math.random(), name:(t.name||'').trim(), amount: Number(t.amount)||0, from:t.from||'', to:t.to||'' }))
      // Require a NAME plus either rent or dates. A row without a name is noise and
      // gets dropped — no ghost dates, no ghost amounts, no ghost names.
      .filter(t => t.name && (t.amount > 0 || (t.from && t.to)));
    const rentSum = cleanTenants.reduce((s,t)=>s+t.amount,0);
    const nameJoined = cleanTenants.map(t=>t.name).filter(Boolean).join(' · ');
    setMonthly(p=>({...p,[key]:{
      ...(existing.leaseCostAtRecord != null ? {} : contractSnapshot(selected.unitId)),
      ...form,
      tenants: cleanTenants,
      // Legacy fields kept in sync so older views/exports keep working.
      tenantName:     nameJoined,
      rentCollected:  rentSum,
      electricityCost: Number(form.electricityCost)||0,
      maintenanceItems:items,
      maintenanceCost: calcMaintTotal(items),
    }}));
    setSelected(null);
  }

  // Maintenance items modal functions
  function openMaint(e, unitId, monthKey){ e.stopPropagation(); setNewItem({desc:"",amount:""}); setMaintModal({unitId,monthKey}); }

  function addMaintItem(){
    if(!newItem.desc||!newItem.amount) return;
    const key=`${maintModal.unitId}-${maintModal.monthKey}`;
    const op=getOp(maintModal.unitId,maintModal.monthKey);
    const items=[...(op.maintenanceItems||[]),{id:Date.now(),desc:newItem.desc,amount:Number(newItem.amount)}];
    setMonthly(p=>({...p,[key]:{
      ...(op.leaseCostAtRecord != null ? {} : contractSnapshot(maintModal.unitId)),
      ...op,
      maintenanceItems:items,
      maintenanceCost:calcMaintTotal(items),
    }}));
    setNewItem({desc:"",amount:""});
  }

  function deleteMaintItem(itemId){
    const key=`${maintModal.unitId}-${maintModal.monthKey}`;
    const op=getOp(maintModal.unitId,maintModal.monthKey);
    const items=(op.maintenanceItems||[]).filter(i=>i.id!==itemId);
    setMonthly(p=>({...p,[key]:{...op,maintenanceItems:items,maintenanceCost:calcMaintTotal(items)}}));
  }

  // Monthly summary stats
  // G&A overhead hits-month helper (mirrors Finance.overheadHitsMonth):
  // honours freq (once / monthly / quarterly / semi-annual / annual) + startDate / endDate.
  function overheadHits(entry, mKey){
    if (!entry || !entry.startDate) return false;
    const startMonth = String(entry.startDate).slice(0,7);
    if (mKey < startMonth) return false;
    const endMonth = entry.endDate ? String(entry.endDate).slice(0,7) : '';
    if (endMonth && mKey > endMonth) return false;
    const freq = entry.freq || 'once';
    if (freq === 'once')    return mKey === startMonth;
    if (freq === 'monthly') return true;
    const [sY,sM] = startMonth.split('-').map(Number);
    const [mY,mM] = mKey.split('-').map(Number);
    const diff = (mY-sY)*12 + (mM-sM);
    if (freq === 'quarterly')   return diff >= 0 && diff % 3 === 0;
    if (freq === 'semi-annual') return diff >= 0 && diff % 6 === 0;
    if (freq === 'annual')      return diff >= 0 && diff % 12 === 0;
    return false;
  }

  const monthStats = useMemo(()=>{
    let totalRent=0,totalMaint=0,totalElec=0,occupied=0,vacant=0,total=0;
    unitRows.forEach(u=>{
      const op=getOp(u.id,viewMonth);
      if(op.status==="—") return;
      total++;
      totalMaint += Number(op.maintenanceCost)||0;
      totalElec  += Number(op.electricityCost)||0;
      if(op.status==="مشغول"){ occupied++; totalRent+=Number(op.rentCollected)||0; }
      else vacant++;
    });
    const totalOverhead = (overhead||[]).reduce((s,e)=>overheadHits(e,viewMonth)?s+(Number(e.amount)||0):s,0);
    return{totalRent,totalMaint,totalElec,totalOverhead,occupied,vacant,total};
  },[unitRows,monthly,viewMonth,overhead]);

  const selectedUnit = selected ? unitRows.find(u=>u.id===selected.unitId) : null;

  return(
    <div style={{padding:"24px 28px",maxWidth:1400,margin:"0 auto"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20,flexWrap:"wrap",gap:12}}>
        <div>
          <h2 style={{fontSize:20,fontWeight:800,color:"#c8a03c",margin:0}}>{T.title}</h2>
          <p style={{fontSize:13,color:"#475569",margin:"4px 0 0"}}>{T.subtitle}</p>
        </div>
        {/* Month selector */}
        <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
          {getMonths(lang).map(m=>(
            <button key={m.key} onClick={()=>setViewMonth(m.key)} style={{padding:"6px 12px",borderRadius:10,border:"1px solid",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"'Tajawal',sans-serif",
              background:viewMonth===m.key?"rgba(200,160,60,0.2)":"rgba(255,255,255,0.03)",
              color:viewMonth===m.key?"#c8a03c":"#64748b",
              borderColor:viewMonth===m.key?"rgba(200,160,60,0.4)":"rgba(255,255,255,0.06)",
              outline:m.key==="2026-04"&&viewMonth==="2026-04"?"none":"none"}}>
              {m.short}
            </button>
          ))}
        </div>
      </div>

      {/* Month Summary Cards */}
      <div style={{display:"flex",gap:12,marginBottom:24,flexWrap:"wrap"}}>
        <StatCard label={T.totalUnits}           value={monthStats.total}                       accent="#e2e8f0" icon="🏢"/>
        <StatCard label={T.occupied}             value={monthStats.occupied}                    accent="#22c55e" icon="✅"/>
        <StatCard label={T.vacant}               value={monthStats.vacant}                      accent="#38bdf8" icon="🔑"/>
        <StatCard label={T.revenue}              value={monthStats.totalRent.toLocaleString()}  accent="#c8a03c" icon="💰"/>
        <StatCard label="فاتورة الكهرباء"          value={monthStats.totalElec.toLocaleString()}      accent="#fbbf24" icon="⚡"/>
        <StatCard label="المصاريف الأخرى"          value={monthStats.totalMaint.toLocaleString()}     accent="#f97316" icon="💸"/>
        <StatCard label="المصاريف الإدارية"        value={monthStats.totalOverhead.toLocaleString()}  accent="#a78bfa" icon="🏢"/>
      </div>

      {/* Full Year Grid */}
      <div style={{marginBottom:28}}>
        <div style={{fontSize:13,color:"#475569",fontWeight:700,marginBottom:12}}>{T.yearOverview}</div>
        <div style={{overflowX:"auto"}}>
          <table style={{borderCollapse:"collapse",minWidth:900,width:"100%"}}>
            <thead>
              <tr style={{background:"rgba(0,0,0,0.3)"}}>
                <th style={{padding:"10px 14px",textAlign:"right",fontSize:11,color:"#475569",fontWeight:700,whiteSpace:"nowrap",minWidth:140,position:"sticky",right:0,background:"rgba(13,15,26,0.95)"}}>الوحدة</th>
                {MONTHS.map(m=>(
                  <th key={m.key} style={{padding:"10px 12px",textAlign:"center",fontSize:11,color:m.key===viewMonth?"#c8a03c":"#475569",fontWeight:700,whiteSpace:"nowrap",background:m.key===viewMonth?"rgba(200,160,60,0.06)":"transparent",borderBottom:m.key===viewMonth?"2px solid rgba(200,160,60,0.4)":"none"}}>
                    {m.short}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {unitRows.map(u=>(
                <tr key={u.id} style={{borderTop:"1px solid rgba(255,255,255,0.04)"}}>
                  <td style={{padding:"8px 14px",position:"sticky",right:0,background:"rgba(13,15,26,0.95)",zIndex:2}}>
                    <div style={{fontSize:14,fontWeight:700,color:"#c8a03c"}}>{u.unitNumber}</div>
                    <div style={{fontSize:11,color:"#475569"}}>{u.district}</div>
                  </td>
                  {MONTHS.map(m=>{
                    const op=getOp(u.id,m.key);
                    const cfg=OP_STATUS[op.status]||OP_STATUS["—"];
                    const isCur=m.key===viewMonth;
                    const hasData=op.status!=="—";
                    return(
                      <td key={m.key} style={{padding:"6px",textAlign:"center",background:isCur?"rgba(200,160,60,0.03)":"transparent"}}>
                        <div className="cell-hover" onClick={()=>openCell(u.id,m.key)}
                          style={{padding:"6px 4px",borderRadius:8,background:hasData?cfg.bg:"rgba(255,255,255,0.02)",border:`1px solid ${hasData?cfg.border:"rgba(255,255,255,0.05)"}`,minWidth:52,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                          <span style={{fontSize:11,fontWeight:700,color:hasData?cfg.color:"#334155"}}>{op.status}</span>
                          {op.rentCollected>0&&<span style={{fontSize:9,color:"#c8a03c"}}>{Number(op.rentCollected).toLocaleString()}</span>}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Current Month Detail List */}
      <div>
        <div style={{fontSize:13,color:"#475569",fontWeight:700,marginBottom:12}}>{lang==="en"?`${getMonths(lang).find(m=>m.key===viewMonth)?.label} Details — Click row to edit`:(`تفاصيل شهر ${curMonth?.label} — انقر على صف للتعديل`)}</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:10}}>
          {unitRows.map(u=>{
            const op=getOp(u.id,viewMonth);
            const cfg=OP_STATUS[op.status]||OP_STATUS["—"];
            return(
              <div key={u.id} style={{background:"rgba(255,255,255,0.02)",border:`1px solid ${op.status!=="—"?cfg.border:"rgba(255,255,255,0.06)"}`,borderRadius:12,padding:"14px 16px",position:"relative"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}} onClick={()=>openCell(u.id,viewMonth)} className="cell-hover">
                  <div>
                    <span style={{fontSize:16,fontWeight:800,color:"#c8a03c"}}>{u.unitNumber}</span>
                    <span style={{fontSize:11,color:"#475569",marginRight:8}}>{u.district}</span>
                  </div>
                  <span style={{fontSize:11,fontWeight:700,color:cfg.color,background:cfg.bg,padding:"2px 9px",borderRadius:12}}>{op.status}</span>
                </div>
                {op.tenantName&&<div style={{fontSize:13,color:"#e2e8f0",marginBottom:6}} onClick={()=>openCell(u.id,viewMonth)} className="cell-hover">{op.tenantName}</div>}

                {/* Financial row */}
                <div style={{display:"flex",gap:8,fontSize:12,flexWrap:"wrap",alignItems:"center",marginBottom:op.maintenanceItems?.length>0?8:0}}>
                  {op.rentCollected>0&&<span style={{color:"#22c55e",cursor:"pointer"}} onClick={()=>openCell(u.id,viewMonth)}>💰 {Number(op.rentCollected).toLocaleString()} ر</span>}
                  {op.electricityCost>0&&<span style={{color:"#fbbf24",cursor:"pointer"}} onClick={()=>openCell(u.id,viewMonth)}>⚡ {Number(op.electricityCost).toLocaleString()} ر</span>}
                  {/* Other expenses (flexible list) with + button */}
                  <div style={{display:"flex",alignItems:"center",gap:4}}>
                    {op.maintenanceCost>0&&<span style={{color:"#f97316"}}>💸 {Number(op.maintenanceCost).toLocaleString()} ر</span>}
                    <button onClick={e=>openMaint(e,u.id,viewMonth)}
                      style={{width:20,height:20,borderRadius:"50%",background:"rgba(249,115,22,0.15)",border:"1px solid rgba(249,115,22,0.3)",color:"#f97316",fontSize:13,fontWeight:800,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1,padding:0,fontFamily:"'Tajawal',sans-serif"}}>
                      +
                    </button>
                  </div>
                  {ownership&&ownership[u.id]&&<span style={{color:"#a78bfa",fontSize:11}}>{T.partnership}</span>}
                </div>

                {/* Maintenance items list */}
                {op.maintenanceItems?.length>0&&(
                  <div style={{borderTop:"1px solid rgba(249,115,22,0.1)",paddingTop:6,marginBottom:4}}>
                    {op.maintenanceItems.map(item=>(
                      <div key={item.id} style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"#94a3b8",padding:"2px 0"}}>
                        <span>{item.desc}</span>
                        <span style={{color:"#f97316",fontWeight:600}}>{Number(item.amount).toLocaleString()} ر</span>
                      </div>
                    ))}
                  </div>
                )}

                {op.notes&&<div style={{fontSize:11,color:"#475569",marginTop:4,borderTop:"1px solid rgba(255,255,255,0.04)",paddingTop:5,cursor:"pointer"}} onClick={()=>openCell(u.id,viewMonth)}>{op.notes}</div>}
                <div style={{position:"absolute",top:10,left:12,fontSize:12,opacity:0.2,cursor:"pointer"}} onClick={()=>openCell(u.id,viewMonth)}>✏️</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Edit Cell Modal */}
      {selected&&selectedUnit&&(() => {
        // Lock from/to date pickers to the selected month only — prevents entering dates outside this month by mistake.
        const _mk = selected.monthKey;
        const [_y, _m] = _mk.split('-').map(Number);
        const _lastDay = new Date(_y, _m, 0).getDate();
        const monthFirst = `${_mk}-01`;
        const monthLast  = `${_mk}-${String(_lastDay).padStart(2,'0')}`;
        return (
        <Modal title={`وحدة ${selectedUnit.unitNumber} — ${MONTHS.find(m=>m.key===selected.monthKey)?.label}`} onClose={()=>setSelected(null)}>
          <div style={{marginBottom:14}}>
            <label style={{display:"block",fontSize:12,color:"#94a3b8",marginBottom:8}}>{T.opStatus}</label>
            <div style={{display:"flex",gap:8}}>
              {["مشغول","شاغر","—"].map(s=>{
                const cfg=OP_STATUS[s];
                return(
                  <button key={s} onClick={()=>setForm(f=>({...f,status:s}))}
                    style={{flex:1,padding:"8px 4px",borderRadius:10,border:"1px solid",cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"'Tajawal',sans-serif",
                      background:form.status===s?cfg.bg:"rgba(255,255,255,0.03)",
                      color:form.status===s?cfg.color:"#475569",
                      borderColor:form.status===s?cfg.border:"rgba(255,255,255,0.07)"}}>
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Tenants list — supports multiple tenants per unit per month (short-stay, then another renter). */}
          <div style={{background:"rgba(34,197,94,0.05)",border:"1px solid rgba(34,197,94,0.18)",borderRadius:10,padding:"10px 14px",marginBottom:14}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <span style={{fontSize:12,color:"#94a3b8",fontWeight:700}}>👥 المستأجرين (يمكن إضافة أكثر من واحد)</span>
              <span style={{fontSize:14,fontWeight:700,color:"#22c55e"}}>
                المجموع: {(form.tenants||[]).reduce((s,t)=>s+(Number(t.amount)||0),0).toLocaleString()} ر
              </span>
            </div>
            {(form.tenants||[]).map((t,idx)=>{
              // Compute days occupied for this tenant row
              const days = (()=>{
                if(!t.from||!t.to) return null;
                const d1=new Date(t.from), d2=new Date(t.to);
                if(isNaN(d1)||isNaN(d2)) return null;
                return Math.max(0, Math.round((d2-d1)/(1000*60*60*24))+1);
              })();
              return (
                <div key={t.id||idx} style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:8,padding:"8px",marginBottom:6}}>
                  <div style={{display:"flex",gap:6,marginBottom:5,alignItems:"center"}}>
                    <input placeholder="اسم المستأجر" value={t.name||""} onChange={e=>{
                      const name=e.target.value;
                      setForm(f=>({...f,tenants:(f.tenants||[]).map((x,i)=>i===idx?{...x,name}:x)}));
                    }} style={{flex:2,padding:"7px 10px",borderRadius:7,background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.08)",color:"#e2e8f0",fontSize:13,fontFamily:"'Tajawal',sans-serif"}}/>
                    <input placeholder="الإيجار" type="number" value={t.amount||""} onChange={e=>{
                      const amount=e.target.value;
                      setForm(f=>({...f,tenants:(f.tenants||[]).map((x,i)=>i===idx?{...x,amount}:x)}));
                    }} style={{flex:1,padding:"7px 10px",borderRadius:7,background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.08)",color:"#e2e8f0",fontSize:13}}/>
                    {(form.tenants||[]).length>1 && (
                      <button onClick={()=>setForm(f=>({...f,tenants:(f.tenants||[]).filter((_,i)=>i!==idx)}))}
                        style={{background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.25)",color:"#ef4444",borderRadius:7,padding:"5px 9px",cursor:"pointer",fontSize:12}}>×</button>
                    )}
                  </div>
                  <div style={{display:"flex",gap:6,alignItems:"center",fontSize:11,color:"#64748b"}}>
                    <span>من:</span>
                    {/* from = must be inside the selected month */}
                    <input type="date" value={t.from||""} min={monthFirst} max={monthLast} onChange={e=>{
                      const from=e.target.value;
                      setForm(f=>({...f,tenants:(f.tenants||[]).map((x,i)=>i===idx?{...x,from}:x)}));
                    }} style={{flex:1,padding:"5px 8px",borderRadius:6,background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.08)",color:"#e2e8f0",fontSize:12,colorScheme:"dark"}}/>
                    <span>إلى:</span>
                    {/* to = must be ≥ from (or ≥ month start if from not set yet); no upper cap — rental can extend into future months */}
                    <input type="date" value={t.to||""} min={t.from || monthFirst} onChange={e=>{
                      const to=e.target.value;
                      setForm(f=>({...f,tenants:(f.tenants||[]).map((x,i)=>i===idx?{...x,to}:x)}));
                    }} style={{flex:1,padding:"5px 8px",borderRadius:6,background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.08)",color:"#e2e8f0",fontSize:12,colorScheme:"dark"}}/>
                    {days!==null && <span style={{color:"#22c55e",fontWeight:700,minWidth:50,textAlign:"center"}}>{days} يوم</span>}
                  </div>
                </div>
              );
            })}
            <button onClick={()=>setForm(f=>({...f,tenants:[...(f.tenants||[]),{id:Date.now(),name:"",amount:"",from:"",to:""}]}))}
              style={{marginTop:4,width:"100%",padding:"6px",borderRadius:7,background:"rgba(34,197,94,0.1)",border:"1px solid rgba(34,197,94,0.3)",color:"#22c55e",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>
              + إضافة مستأجر آخر
            </button>
          </div>
          {/* Electricity — dedicated fixed slot (per Ibrahim's design) */}
          <Field label="⚡ فاتورة الكهرباء" value={form.electricityCost||""} onChange={v=>setForm(f=>({...f,electricityCost:v}))} type="number"/>
          {/* Other expenses — flexible list managed via + button on card. Historically labelled "maintenance"; broadened to cover internet, cleaning, services, etc. */}
          <div style={{background:"rgba(249,115,22,0.06)",border:"1px solid rgba(249,115,22,0.18)",borderRadius:10,padding:"10px 14px",marginBottom:14}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontSize:12,color:"#94a3b8"}}>💸 المصاريف الأخرى</span>
              <span style={{fontSize:15,fontWeight:700,color:"#f97316"}}>{calcMaintTotal(form.maintenanceItems||[]).toLocaleString()} ريال</span>
            </div>
            {(form.maintenanceItems||[]).length>0&&(
              <div style={{marginTop:8,borderTop:"1px solid rgba(249,115,22,0.1)",paddingTop:8}}>
                {(form.maintenanceItems||[]).map(item=>(
                  <div key={item.id} style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#94a3b8",padding:"2px 0"}}>
                    <span>{item.desc}</span><span style={{color:"#f97316"}}>{Number(item.amount).toLocaleString()} ر</span>
                  </div>
                ))}
              </div>
            )}
            <button onClick={e=>{saveOp();setTimeout(()=>openMaint({stopPropagation:()=>{}},selected?.unitId,selected?.monthKey),50);}}
              style={{marginTop:8,width:"100%",padding:"5px",borderRadius:8,background:"rgba(249,115,22,0.1)",border:"1px solid rgba(249,115,22,0.25)",color:"#f97316",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>
              {T.addExpenses}
            </button>
          </div>
          <Field label={T.monthNotes} value={form.notes||""} onChange={v=>setForm(f=>({...f,notes:v}))} multiline rows={2}/>
          <SaveBtn onClick={saveOp}/>
        </Modal>
        );
      })()}

      {/* Maintenance Items Modal */}
      {maintModal && <MaintModal
        maintModal={maintModal}
        monthly={monthly}
        units={unitRows}
        newItem={newItem}
        setNewItem={setNewItem}
        onClose={()=>setMaintModal(null)}
        onAdd={addMaintItem}
        onDelete={deleteMaintItem}
        getOp={getOp}
        calcMaintTotal={calcMaintTotal}
      />}
    </div>
  );
}

function MaintModal({ maintModal, monthly, units, newItem, setNewItem, onClose, onAdd, onDelete, getOp, calcMaintTotal }){
  const {S, lang} = useLang();
  const T = S.monthly;
  const mOp   = getOp(maintModal.unitId, maintModal.monthKey);
  const mUnit = units.find(u=>u.id===maintModal.unitId);
  const mMonth= MONTHS.find(m=>m.key===maintModal.monthKey);
  const items = mOp.maintenanceItems||[];
  const total = calcMaintTotal(items);
  return(
    <Modal title={`المصاريف الأخرى — وحدة ${mUnit?.unitNumber} · ${mMonth?.label}`} onClose={onClose}>
      <div style={{background:"rgba(249,115,22,0.07)",border:"1px solid rgba(249,115,22,0.2)",borderRadius:10,padding:"12px 16px",marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <span style={{fontSize:13,color:"#94a3b8"}}>{T.maintModalTotal}</span>
        <span style={{fontSize:20,fontWeight:800,color:"#f97316"}}>{total.toLocaleString()} <span style={{fontSize:12,fontWeight:400}}>ريال</span></span>
      </div>
      {items.length>0 && (
        <div style={{marginBottom:16}}>
          {items.map(item=>(
            <div key={item.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 14px",borderRadius:9,marginBottom:6,background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)"}}>
              <span style={{fontSize:13,color:"#e2e8f0",flex:1}}>{item.desc}</span>
              <span style={{fontSize:13,fontWeight:700,color:"#f97316",marginLeft:12,marginRight:12}}>{Number(item.amount).toLocaleString()} ريال</span>
              <button onClick={()=>onDelete(item.id)} style={{background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.2)",color:"#ef4444",borderRadius:7,padding:"3px 8px",cursor:"pointer",fontSize:12,fontFamily:"'Tajawal',sans-serif"}}>{T.deleteBtn}</button>
            </div>
          ))}
        </div>
      )}
      {items.length===0 && <div style={{textAlign:"center",padding:"16px 0",color:"#334155",fontSize:13}}>{T.noExpenses}</div>}
      <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:10,padding:"14px"}}>
        <div style={{fontSize:12,color:"#94a3b8",marginBottom:10,fontWeight:700}}>{T.addExpense}</div>
        <Field label={T.expenseDesc} value={newItem.desc} onChange={v=>setNewItem(f=>({...f,desc:v}))} required/>
        <Field label={T.expenseAmt} value={newItem.amount} onChange={v=>setNewItem(f=>({...f,amount:v}))} type="number" required/>
        <button onClick={onAdd} disabled={!newItem.desc||!newItem.amount}
          style={{width:"100%",padding:"9px",borderRadius:9,background:(!newItem.desc||!newItem.amount)?"rgba(255,255,255,0.04)":"rgba(249,115,22,0.15)",border:"1px solid",borderColor:(!newItem.desc||!newItem.amount)?"rgba(255,255,255,0.08)":"rgba(249,115,22,0.35)",color:(!newItem.desc||!newItem.amount)?"#334155":"#f97316",fontSize:13,fontWeight:700,cursor:(!newItem.desc||!newItem.amount)?"not-allowed":"pointer",fontFamily:"'Tajawal',sans-serif"}}>
          {T.addBtn}
        </button>
      </div>
    </Modal>
  );
}
